import { Component, Vue } from "vue-property-decorator";
import QuoteDraftService from "../../services/quote_draft_service";
import { SalesOrderSummary } from "../../models/quote_draft/quote_draft";
import { PAGE_SIZE } from "../../utility/pagination";

/** Search re-queries NetSuite; wait for a pause in typing before asking. */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * FUPOF-200 — a sales order list paged by the API, shared by the Sales Orders
 * page and the Sales Orders tab on a customer's page.
 *
 * Paged server-side, not in the browser like the quote and customer tables: a
 * single rep has up to 7,507 sales orders (57,681 account-wide, Sandbox
 * 2026-09-29), more than one NetSuite read returns. So each page and each
 * search is a request, and search runs on the server over the whole scope.
 *
 * A view narrows the list by overriding `salesOrderScope()` — the customer page
 * returns its customer id — and drives it with `loadSalesOrders()`,
 * `changeSalesOrderPage()` and `queueSalesOrderSearch()`.
 */
@Component
export class SalesOrderPaging extends Vue {
  protected salesOrders: SalesOrderSummary[] = [];
  /** Orders matching the current search across every page. */
  protected salesOrdersTotal = 0;
  /** Orders with no search applied — the count a tab pill should show. */
  protected salesOrdersBrowseTotal = 0;
  protected salesOrdersPage = 1;
  protected salesOrdersPerPage = PAGE_SIZE;
  /** The search term the rows on screen answer. */
  protected salesOrderSearchTerm = "";
  /** True from the first keystroke of a debounced search until it lands. */
  protected salesOrdersLoading = false;

  /** A search typed but not yet on screen; null when none is pending. */
  private pendingSalesOrderSearch: string | null = null;
  private salesOrderSearchTimer: number | null = null;
  /** Bumped per request so a slow, superseded response can't overwrite a newer one. */
  private salesOrderSequence = 0;
  private salesOrderQueryService = new QuoteDraftService();

  /** Extra filters for every request; overridden by a customer-scoped view. */
  protected salesOrderScope(): { customer_id?: number } {
    return {};
  }

  protected async loadSalesOrders(
    page = 1,
    term: string = this.salesOrderSearchTerm
  ): Promise<void> {
    const sequence = ++this.salesOrderSequence;
    this.salesOrdersLoading = true;
    let rows: SalesOrderSummary[] = [];
    let total = 0;
    try {
      const result = await this.salesOrderQueryService.listSalesOrders({
        ...this.salesOrderScope(),
        page,
        per_page: this.salesOrdersPerPage,
        search: term === "" ? undefined : term
      });
      rows = result.sales_orders;
      total = result.total;
    } catch (err) {
      // Non-fatal, and consistent with how the quotes list treats an
      // unreachable source: an empty list rather than an error page the rep
      // can do nothing with.
      console.warn("Sales orders unavailable:", err);
    }
    if (sequence !== this.salesOrderSequence) return;
    this.salesOrders = rows;
    this.salesOrdersTotal = total;
    this.salesOrdersPage = page;
    this.salesOrderSearchTerm = term;
    this.pendingSalesOrderSearch = null;
    if (term === "") {
      this.salesOrdersBrowseTotal = total;
    }
    this.salesOrdersLoading = false;
  }

  protected changeSalesOrderPage(page: number): void {
    this.loadSalesOrders(page);
  }

  /**
   * Re-query page 1 for `rawTerm` once typing pauses. A no-op when the term is
   * what is already on screen or already on its way, so re-announcing the same
   * term (a tab switch, say) can't cancel a page the rep just asked for.
   */
  protected queueSalesOrderSearch(rawTerm: string): void {
    const term = rawTerm.trim();
    if (term === (this.pendingSalesOrderSearch ?? this.salesOrderSearchTerm)) {
      return;
    }
    if (this.salesOrderSearchTimer !== null) {
      window.clearTimeout(this.salesOrderSearchTimer);
      this.salesOrderSearchTimer = null;
    }
    if (term === this.salesOrderSearchTerm) {
      // Typed back to what is on screen: drop the search still pending.
      this.salesOrderSequence++;
      this.pendingSalesOrderSearch = null;
      this.salesOrdersLoading = false;
      return;
    }
    this.pendingSalesOrderSearch = term;
    this.salesOrdersLoading = true;
    this.salesOrderSearchTimer = window.setTimeout(() => {
      this.salesOrderSearchTimer = null;
      this.loadSalesOrders(1, term);
    }, SEARCH_DEBOUNCE_MS);
  }

  beforeDestroy(): void {
    if (this.salesOrderSearchTimer !== null) {
      window.clearTimeout(this.salesOrderSearchTimer);
    }
  }
}
